'use client'

import type { ApolloBrowserXClientValues } from '@/feature/app/services/app.constants'
import { createHttpLink } from '@apollo/client'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { getCookie } from 'cookies-next'
import type { RuntimeVariables } from '../_helpers/getEnvironmentHeaders'
import { getBuildTimeHeaders } from '../_helpers/getEnvironmentHeaders'

if (process.env.NODE_ENV !== 'production') {
  loadDevMessages()
  loadErrorMessages()
}

if (process.env.NODE_ENV !== 'production') {
  loadDevMessages()
  loadErrorMessages()
}

/**
 * Creates an HTTP link configuration for Apollo Client in the browser environment.
 *
 * @remarks
 * This link is configured with default HTTP headers and specific client identification.
 *
 * @returns An ApolloLink instance configured for browser HTTP requests
 */
export const makeBrowserHttpLink = (
  { graphqlApiUrl, appEnvironment }: RuntimeVariables,
  xClientValue: ApolloBrowserXClientValues,
) => {
  const isDev = process.env.NODE_ENV === 'development'

  // Read cookies once
  const includeMockInstructions = isDev && !!getCookie('x-mock-instructions')
  // Get noResponseCache cookie value to determine if response caching should be disabled
  const noResponseCacheCookie = getCookie('noResponseCache') === '1'

  // Disable response cache if in dev with mock instructions or if the cookie is set to '1'
  const disableResponseCache = includeMockInstructions || noResponseCacheCookie

  return createHttpLink({
    uri:
      graphqlApiUrl ||
      process.env.DC_URL_API_SERVER ||
      process.env.NEXT_PUBLIC_DC_URL_API_CLIENT ||
      '/graphql',
    fetchOptions: { cache: 'no-store' },
    credentials: 'include',
    headers: {
      'x-client': xClientValue,
      'x-environment': appEnvironment,
      ...getBuildTimeHeaders(),
      // If in development mode and mock instructions exist, include the header
      ...(isDev &&
        includeMockInstructions && {
          'x-mock-instructions': String(includeMockInstructions),
        }),
      // If response cache should be disabled, include the header once
      ...(disableResponseCache && { 'x-no-responsecache': 'true' }),
    },
  })
}
